// views/Dashboard/Profile/components/StudentAgendaTab.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Button, Spinner, Center, Icon, Flex, useToast,
} from '@chakra-ui/react';
import { FiCalendar, FiClock, FiInbox, FiPlus } from 'react-icons/fi';
import { getStudentAppointments, updateAppointmentStatus, cancelAppointment } from 'services/appointmentBooking';
import { useUserAuth } from 'utils/site';
import EventModal from '../../Agenda/components/EventModal';

const CITA_STATUS = {
  pending:   { bg: 'butter.bg', color: 'butter.ink', label: 'Pendiente' },
  confirmed: { bg: 'sky.bg',    color: 'sky.ink',    label: 'Confirmada' },
  completed: { bg: 'mint.bg',   color: 'mint.ink',   label: 'Realizada' },
  cancelled: { bg: 'rose.bg',   color: 'rose.ink',   label: 'Cancelada' },
};

const buildEvent = (apt) => ({
  id: `appt-${apt.id}`,
  title: `🗓 ${apt.student_name || apt.guardian_name || 'Sin nombre'}`,
  start_date: `${apt.appointment_date}T${apt.start_time}`,
  end_date: `${apt.appointment_date}T${apt.end_time}`,
  category: 'cita',
  status: apt.status,
  all_day: false,
  _appointment: apt,
});

const StudentAgendaTab = ({ customer }) => {
  const { user: authUser } = useUserAuth();
  const toast = useToast();
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadCitas = useCallback(async () => {
    if (!customer?.username) {
      setError('Username no disponible');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getStudentAppointments(customer.username, {});
      const items = Array.isArray(data) ? data : (data?.data || []);
      setCitas([...items].sort((a, b) => {
        const da = new Date(`${a.appointment_date}T${a.start_time}`);
        const db = new Date(`${b.appointment_date}T${b.start_time}`);
        return db - da;
      }));
    } catch (err) {
      setError(err.message || 'Error al cargar la agenda');
      setCitas([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.username]);

  useEffect(() => { loadCitas(); }, [loadCitas]);

  const handleNewCita = () => {
    if (!authUser?.id || !customer?.username) return;
    const url = `${window.location.origin}/#/public/reservar-hora/${authUser.id}?student_rut=${encodeURIComponent(customer.username)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleView = (apt) => {
    setSelectedEvent(buildEvent(apt));
    setShowModal(true);
  };

  const handleStatusChange = async (appointment, newStatus, notes = '') => {
    try {
      if (newStatus === 'cancelled') {
        await cancelAppointment(appointment.id, notes);
      } else {
        await updateAppointmentStatus(appointment.id, newStatus, notes);
      }
      const labels = { confirmed: 'confirmada', completed: 'completada', cancelled: 'cancelada' };
      toast({ title: `Cita ${labels[newStatus] || 'actualizada'}`, status: 'success', duration: 3000, isClosable: true });
      setShowModal(false);
      loadCitas();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000, isClosable: true });
      throw err;
    }
  };

  const handleSaveTeacherNotes = async (appointment, notes) => {
    try {
      await updateAppointmentStatus(appointment.id, appointment.status, notes);
      toast({ title: 'Nota guardada', status: 'success', duration: 2500, isClosable: true });
      loadCitas();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000, isClosable: true });
      throw err;
    }
  };

  if (loading) {
    return (
      <Center py={12} fontFamily="jakarta">
        <VStack>
          <Spinner size="lg" color="coral.500" />
          <Text color="ink.400" fontSize="13.5px">Cargando agenda...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Box bg="rose.bg" borderRadius="14px" p={4} m={6} fontFamily="jakarta">
        <Text fontWeight="700" fontSize="13.5px" color="rose.ink">Error al cargar la agenda</Text>
        <Text fontSize="12.5px" color="rose.ink">{error}</Text>
      </Box>
    );
  }

  return (
    <Box p={6} fontFamily="jakarta">
      <VStack align="stretch" spacing={5}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={2}>
            <Flex w="34px" h="34px" borderRadius="10px" bg="mint.bg" align="center" justify="center">
              <Icon as={FiCalendar} color="mint.ink" boxSize={4} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontSize="15px" fontWeight="800" color="ink.900">
                Agenda de {customer?.first_name} {customer?.last_name}
              </Text>
              <Text fontSize="12px" color="ink.400" fontWeight="500">
                {citas.length} cita{citas.length !== 1 ? 's' : ''} registrada{citas.length !== 1 ? 's' : ''}
              </Text>
            </VStack>
          </HStack>

          {authUser?.id && (
            <Button size="sm" borderRadius="9px" fontWeight="700" fontSize="12.5px"
              bg="coral.500" color="white" _hover={{ bg: 'coral.600' }}
              leftIcon={<Icon as={FiPlus} boxSize="14px" />}
              onClick={handleNewCita}
            >
              Nueva cita
            </Button>
          )}
        </Flex>

        {citas.length === 0 ? (
          <VStack py={12} px={6} bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2" spacing={3}>
            <Icon as={FiInbox} boxSize={9} color="ink.400" />
            <Text fontSize="14px" fontWeight="800" color="ink.900">Sin citas registradas</Text>
            <Text fontSize="12.5px" color="ink.400" textAlign="center" maxW="380px">
              Las citas agendadas con este alumno aparecerán aquí.
            </Text>
          </VStack>
        ) : (
          <VStack align="stretch" spacing={3}>
            {citas.map((c) => {
              const chip = CITA_STATUS[c.status] || CITA_STATUS.pending;
              const d = new Date(`${c.appointment_date}T${c.start_time || '00:00:00'}`);
              const dia = d.toLocaleDateString('es-CL', { day: '2-digit' });
              const mes = d.toLocaleDateString('es-CL', { month: 'short' }).replace('.', '');
              const hora = c.start_time ? `${c.start_time.slice(0, 5)} — ${c.end_time?.slice(0, 5) || ''}` : '';

              return (
                <Flex key={c.id} bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px"
                  p={4} gap={3} align="center" cursor="pointer"
                  transition="background .13s, border-color .13s"
                  _hover={{ bg: 'coral.tint', borderColor: 'coral.soft' }}
                  onClick={() => handleView(c)}
                >
                  <VStack flex="0 0 52px" spacing={0} align="center" bg="cream.bg2" borderRadius="10px" py={2}>
                    <Text fontSize="16px" fontWeight="800" color="ink.900" lineHeight={1}>{dia}</Text>
                    <Text fontSize="10.5px" fontWeight="700" color="ink.400" textTransform="uppercase">{mes}</Text>
                  </VStack>

                  <Box flex={1} minW={0}>
                    <HStack justify="space-between" align="flex-start">
                      <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}>
                        {c.subject || c.notes || 'Cita agendada'}
                      </Text>
                      <Box as="span" px="9px" py="2px" borderRadius="999px" flexShrink={0}
                        bg={chip.bg} color={chip.color} fontSize="11px" fontWeight="700">
                        {chip.label}
                      </Box>
                    </HStack>
                    <HStack spacing={1} mt="3px" color="ink.400">
                      <Icon as={FiClock} boxSize={3} />
                      <Text fontSize="12px" fontWeight="500">{hora}</Text>
                      {c.confirmation_code && (
                        <Text fontSize="11.5px" fontFamily="mono" color="ink.300">· #{c.confirmation_code}</Text>
                      )}
                    </HStack>
                  </Box>
                </Flex>
              );
            })}
          </VStack>
        )}
      </VStack>

      <EventModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        event={selectedEvent}
        mode="view"
        onAppointmentStatusChange={handleStatusChange}
        onSaveTeacherNotes={handleSaveTeacherNotes}
      />
    </Box>
  );
};

export default StudentAgendaTab;
